/**
 * Text that is sized to its box, ported from the legacy `FlexLabel` (Skia): the text gets the
 * largest font size at which it fits the box, and is then aligned in the box. Without `fill`, the
 * height that has to fit is cap height plus descent, so that labels of different text line up;
 * with `fill`, it is the ink of this text above the baseline, and descenders (`,`, `g`) hang below
 * the box, so that they do not change the size. With `fitWidth`, only the height decides the size and a
 * text that is too wide is squeezed horizontally, down to `minScaleX`.
 *
 * Measurements come from a reference size of 1000 px and are scaled, because canvas text metrics
 * are rounded to whole pixels and differ between engines at small sizes. Cap height and descent
 * come from the font file when the style names them (`metrics`), as Skia took them; kerning is off,
 * as in the legacy drawing.
 */

export type HorizontalAlignment = 'left' | 'center' | 'right';
export type VerticalAlignment = 'top' | 'center' | 'bottom';

/** Vertical metrics of a font, as fractions of the font size (from its OS/2 and hhea tables). */
export interface FontMetrics {
    capHeight: number;
    descent: number;
}

export interface TextStyle {
    /** CSS font family, for example `"Titillium Web"`. The font must be loaded before drawing. */
    family: string;
    /** CSS font weight. Default 400. */
    weight?: number;
    /** The font's own metrics. Without them they are measured, which depends on the engine. */
    metrics?: FontMetrics;
    /** Size to the ink of the text above the baseline instead of the font's cap height and descent. Default false. */
    fill?: boolean;
    /** Size by height only; squeeze a text that is too wide. Default false. */
    fitWidth?: boolean;
    /** With `fitWidth`, the narrowest squeeze: a text that would need more is made smaller instead. Default 0, no limit. */
    minScaleX?: number;
    /**
     * The largest font size, in box heights. Default 2, the legacy label's limit, which keeps a text
     * of little ink (a lone `-`) from filling its box; a unit `°` is to fill its own.
     */
    maxSize?: number;
    /** A font size in px to draw at, instead of the one that fits: a table's, the same for every cell. */
    size?: number;
    align?: HorizontalAlignment;
    valign?: VerticalAlignment;
}

export interface Rect {
    left: number;
    top: number;
    width: number;
    height: number;
}

export interface TextLayout {
    font: string;
    /** The font size in px. */
    size: number;
    /** Where to draw with `textBaseline = 'alphabetic'`, relative to the box. */
    x: number;
    baseline: number;
    /** Horizontal squeeze for `fitWidth` (1 when the text fits). */
    scaleX: number;
    /** Where across the box the squeeze is around, relative to it: the point the text is aligned to. */
    squeezeX: number;
    /** The text as drawn, relative to the box (the legacy render bounds). */
    bounds: Rect;
}

type Context = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/** Size at which text is measured; measurements are then scaled to the size in use. */
const REFERENCE_SIZE = 1000;

/** Ink of a text at 1 px font size: left edge, width, ascent above and descent below the baseline. */
interface Ink {
    left: number;
    width: number;
    ascent: number;
    descent: number;
}

const inkCache = new Map<string, Ink>();
/** Every new value is a new text: the cache starts over beyond this, so a dashboard left on for days does not grow it without end. */
const INK_CACHE_LIMIT = 2_000;
const fontMetricsCache = new Map<string, FontMetrics>();

function fontString(style: TextStyle, size: number) : string {
    return `${style.weight ?? 400} ${size}px ${style.family}`;
}

/** Measurements of a font that is still loading are those of a fallback font and must not be kept. */
function isFontLoaded(font: string) : boolean {
    const fonts = (globalThis as { document?: Document }).document?.fonts;

    return fonts === undefined || fonts.check(font);
}

/** Font size of the scratch drawing that finds a text's ink: accurate to about 0.5 %. */
const INK_SCAN_SIZE = 200;

/**
 * A text's ink found from pixels: drawn on a scratch canvas and scanned, as the icons' ink is.
 * WebKit's `measureText` gives the advance box instead of the ink horizontally and clamps the
 * descent at 0 for glyphs above the baseline (`°`), so fitted text came out smaller there and a
 * lone `°` far too small (POC P2, open item 5). Undefined where there is no canvas (tests).
 */
function scanInk(text: string, style: TextStyle) : Ink | undefined {
    if (typeof OffscreenCanvas === 'undefined') {
        return undefined;
    }

    const font = fontString(style, INK_SCAN_SIZE);
    const probe = new OffscreenCanvas(1, 1).getContext('2d');
    if (!probe) {
        return undefined;
    }
    probe.font = font;
    // Room for side bearings, overhangs and marks far above or below the baseline.
    const pad = INK_SCAN_SIZE;
    const width = Math.ceil(probe.measureText(text).width + 2 * pad);
    const height = 3 * INK_SCAN_SIZE;
    const baseline = 2 * INK_SCAN_SIZE;
    const ctx = new OffscreenCanvas(width, height).getContext('2d', { willReadFrequently: true });
    if (!ctx) {
        return undefined;
    }

    ctx.font = font;
    ctx.fontKerning = 'none';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(text, pad, baseline);
    const pixels = ctx.getImageData(0, 0, width, height).data;
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            if ((pixels[(y * width + x) * 4 + 3] ?? 0) > 0) {
                minX = Math.min(minX, x);
                maxX = Math.max(maxX, x);
                minY = Math.min(minY, y);
                maxY = Math.max(maxY, y);
            }
        }
    }
    if (maxX < 0) {
        return { left: 0, width: 0, ascent: 0, descent: 0 };
    }

    return {
        left: (minX - pad) / INK_SCAN_SIZE,
        width: (maxX + 1 - minX) / INK_SCAN_SIZE,
        ascent: (baseline - minY) / INK_SCAN_SIZE,
        descent: (maxY + 1 - baseline) / INK_SCAN_SIZE,
    };
}

function measureInk(ctx: Context, text: string, style: TextStyle) : Ink {
    const key = `${fontString(style, REFERENCE_SIZE)}|${text}`;
    const cached = inkCache.get(key);
    if (cached) {
        return cached;
    }

    ctx.font = fontString(style, REFERENCE_SIZE);
    ctx.fontKerning = 'none';
    const metrics = ctx.measureText(text);
    const ink = scanInk(text, style) ?? {
        left: -metrics.actualBoundingBoxLeft / REFERENCE_SIZE,
        width: (metrics.actualBoundingBoxRight + metrics.actualBoundingBoxLeft) / REFERENCE_SIZE,
        ascent: metrics.actualBoundingBoxAscent / REFERENCE_SIZE,
        descent: metrics.actualBoundingBoxDescent / REFERENCE_SIZE,
    };
    if (isFontLoaded(ctx.font)) {
        if (inkCache.size >= INK_CACHE_LIMIT) {
            inkCache.clear();
        }
        inkCache.set(key, ink);
    }

    return ink;
}

function fontMetrics(ctx: Context, style: TextStyle) : FontMetrics {
    if (style.metrics) {
        return style.metrics;
    }

    const key = fontString(style, REFERENCE_SIZE);
    const cached = fontMetricsCache.get(key);
    if (cached) {
        return cached;
    }

    ctx.font = key;
    const metrics = {
        capHeight: ctx.measureText('H').actualBoundingBoxAscent / REFERENCE_SIZE,
        descent: ctx.measureText('H').fontBoundingBoxDescent / REFERENCE_SIZE,
    };
    if (isFontLoaded(key)) {
        fontMetricsCache.set(key, metrics);
    }

    return metrics;
}

/**
 * How far the ink of `text` reaches below its baseline, in parts of how far it reaches above (0 for
 * none): the room a fitted text with `fill` needs below its box for its descenders.
 */
export function inkHang(ctx: Context, text: string, style: TextStyle) : number {
    const ink = measureInk(ctx, text, style);

    return ink.ascent > 0 ? Math.max(ink.descent, 0) / ink.ascent : 0;
}

/** Finds the font size and position of `text` in a box of `width` × `height`. */
export function layoutText(ctx: Context, text: string, width: number, height: number, style: TextStyle) : TextLayout {
    const ink = measureInk(ctx, text, style);
    const font = fontMetrics(ctx, style);
    const fill = style.fill ?? false;
    const fitWidth = style.fitWidth ?? false;

    // Text height per pixel of font size. With `fill` it is the ink above the baseline, and
    // descenders hang below the box, so that "21,5" gets the size of "21"; a text wholly above the
    // baseline (a lone `°`) fits its ink.
    const heightPerPixel = fill ? ink.ascent + Math.min(ink.descent, 0) : font.capHeight + font.descent;
    const bySize = height * (style.maxSize ?? 2);
    const byHeight = heightPerPixel > 0 ? height / heightPerPixel : bySize;
    // With `fitWidth`, the text is as wide as its box at the narrowest squeeze allowed.
    const minScaleX = fitWidth ? style.minScaleX ?? 0 : 1;
    const byWidth = minScaleX > 0 && ink.width > 0 ? width / (ink.width * minScaleX) : bySize;
    const size = style.size ?? Math.max(1, Math.min(bySize, byHeight, byWidth));

    const textWidth = ink.width * size;
    const textHeight = heightPerPixel * size;
    // Without `fill` the baseline sits at the cap height, rounded up as in the legacy label.
    const ascent = fill ? ink.ascent * size : Math.ceil(font.capHeight * size);

    const align = style.align ?? 'center';
    const valign = style.valign ?? 'center';
    const xOffset = align === 'left' ? 0 : align === 'center' ? width / 2 - textWidth / 2 : width - textWidth;
    const yOffset = valign === 'top' ? 0 : valign === 'center' ? (height - textHeight) / 2 : height - textHeight;
    const scaleX = fitWidth && textWidth > width ? width / textWidth : 1;
    // A squeezed text is squeezed around the point it is aligned to (`drawText`), and its bounds with
    // it: the middle of the box, or the edge, so that a text at the left still starts there.
    const squeezeX = align === 'left' ? 0 : align === 'center' ? width / 2 : width;
    const squeezed = (x: number) : number => squeezeX + (x - squeezeX) * scaleX;

    return {
        font: fontString(style, size),
        size,
        x: xOffset - ink.left * size,
        baseline: ascent + yOffset,
        scaleX,
        squeezeX,
        bounds: { left: squeezed(xOffset + ink.left * size), top: yOffset, width: textWidth * scaleX, height: textHeight },
    };
}

/** Draws `text` fitted to `box` and returns where it went (relative to the box). */
export function drawText(ctx: Context, text: string, box: Rect, color: string, style: TextStyle) : Rect {
    const layout = layoutText(ctx, text, box.width, box.height, style);

    ctx.save();
    ctx.translate(box.left, box.top);
    if (layout.scaleX !== 1) {
        // Centred, squeezed around the middle of the box, as the legacy label does.
        ctx.translate(layout.squeezeX, 0);
        ctx.scale(layout.scaleX, 1);
        ctx.translate(-layout.squeezeX, 0);
    }
    ctx.font = layout.font;
    ctx.fontKerning = 'none';
    ctx.fillStyle = color;
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    ctx.fillText(text, layout.x, layout.baseline);
    ctx.restore();

    return layout.bounds;
}
